'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Trophy, BarChart2, TrendingUp, Menu, X, Bell, Flame, Swords, Shield, Globe } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';


interface TopbarProps {
  activeRoute: string;
}

export default function Topbar({ activeRoute }: TopbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t, language, setLanguage } = useLanguage();
  const { user } = useAuth();

  const isAdmin = !!user && user.user_metadata?.full_name?.toLowerCase() === 'rysias';

  const allNavItems = [
    { labelKey: 'nav_overview' as const, href: '/', icon: BarChart2 },
    { labelKey: 'nav_leaderboard' as const, href: '/leaderboard', icon: TrendingUp },
    { labelKey: 'nav_hall_of_fame' as const, href: '/hall-of-fame', icon: Trophy },
    { labelKey: 'nav_hall_of_shame' as const, href: '/hall-of-shame', icon: Flame },
    { labelKey: 'nav_roastrank' as const, href: '/roastrank', icon: Swords },
    { labelKey: 'nav_roasts_feed' as const, href: '/roastrank/browse', icon: Globe },
    { labelKey: 'nav_admin' as const, href: '/admin', icon: Shield, adminOnly: true },
  ];

  const navItems = allNavItems.filter(item => !item.adminOnly || isAdmin);

  const otherLang = SUPPORTED_LANGUAGES.find(l => l.code !== language)!;

  return (
    <>
      <header
        className="sticky top-0 z-50 w-full"
        style={{
          background: 'rgba(10, 10, 15, 0.92)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3 group">
              <AppLogo size={36} />
              <div className="flex flex-col">
                <span className="font-bold text-base leading-tight" style={{ color: 'var(--foreground)' }}>
                  CommunityStats
                </span>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium leading-tight" style={{ color: 'var(--primary)' }}>
                    Hub
                  </span>
                  {/* WhatsApp logo */}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="#25D366" className="opacity-80">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = activeRoute === item.href;
                const Icon = item.icon;
                return (
                  <Link
                    key={`nav-${item.href}`}
                    href={item.href}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      isActive
                        ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-white/5'
                    }`}
                  >
                    <Icon size={16} />
                    {t(item.labelKey)}
                  </Link>
                );
              })}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {/* Language Toggle */}
              <button
                onClick={() => setLanguage(otherLang.code)}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 hover:bg-white/5"
                style={{ color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}
                title={`Switch to ${otherLang.label}`}
              >
                <span>{otherLang.flag}</span>
                <span>{otherLang.label}</span>
              </button>

              <button
                className="relative p-2 rounded-lg transition-all duration-200 hover:bg-white/5"
                style={{ color: 'var(--muted-foreground)' }}
                aria-label="Notifications"
              >
                <Bell size={20} />
                <span
                  className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full"
                  style={{ background: 'var(--primary)' }}
                />
              </button>

              {/* Live indicator */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full"
                style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
                <span className="pulse-dot" />
                <span className="text-xs font-semibold" style={{ color: 'var(--primary)' }}>
                  {t('nav_live')}
                </span>
              </div>

              {/* Mobile hamburger */}
              <button
                className="md:hidden p-2 rounded-lg transition-all duration-200 hover:bg-white/5"
                style={{ color: 'var(--foreground)' }}
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden animate-fade-in">
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(0,0,0,0.7)' }}
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="absolute top-16 left-0 right-0 p-4 animate-fade-in-up"
            style={{
              background: 'var(--card)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            {navItems.map((item) => {
              const isActive = activeRoute === item.href;
              const Icon = item.icon;
              return (
                <Link
                  key={`mobile-nav-${item.href}`}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 text-sm font-semibold transition-all duration-200 ${
                    isActive
                      ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  }`}
                >
                  <Icon size={18} />
                  {t(item.labelKey)}
                </Link>
              );
            })}
            {/* Mobile language toggle */}
            <button
              onClick={() => { setLanguage(otherLang.code); setMobileOpen(false); }}
              className="flex items-center gap-3 px-4 py-3 rounded-lg mb-1 text-sm font-semibold w-full transition-all duration-200 hover:bg-white/5"
              style={{ color: 'var(--muted-foreground)' }}
            >
              <span className="text-base">{otherLang.flag}</span>
              {otherLang.label}
            </button>
          </div>
        </div>
      )}
    </>
  );
}